import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { avatarTone } from '@shared/utils/avatar-tone';
import { initialsFrom } from '@shared/utils/initials';

/** A person reads as round; an account or a unit is a rounded square. */
export type DefaultAvatarShape = 'circle' | 'rounded';

/**
 * Whole-pixel type, stepped by how big the tile is, rather than a ratio of it.
 *
 * A ratio lands on fractional sizes that rasterize softly and drift — 13.3px at 38px against 14px
 * at 40px is a visible difference between two tiles that should read alike. Each step covers the
 * band of container sizes it still looks right in, so 38, 40 and 42 all draw 14px.
 *
 * `upTo` is inclusive; anything above the last entry uses LARGEST_INITIALS_SIZE.
 */
const INITIALS_SIZE_STEPS: ReadonlyArray<{ upTo: number; fontSize: number }> = [
  { upTo: 26, fontSize: 10 },
  { upTo: 33, fontSize: 12 },
  { upTo: 47, fontSize: 14 },
  { upTo: 59, fontSize: 18 },
  { upTo: 79, fontSize: 24 },
];

const LARGEST_INITIALS_SIZE = 32;

function initialsSize(containerSize: number): number {
  return (
    INITIALS_SIZE_STEPS.find(step => containerSize <= step.upTo)?.fontSize ?? LARGEST_INITIALS_SIZE
  );
}

/**
 * The stand-in shown for an account, a unit or a person that has no picture of its own:
 * initials on a colour derived from the name, or an icon when there is no name to work with.
 *
 * Pair it with `<app-profile-picture>` — picture when there is one, this when there is not.
 */
@Component({
  selector: 'app-default-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon],
  templateUrl: './default-avatar.component.html',
  styleUrls: ['./default-avatar.component.scss'],
  host: {
    // The name is rendered beside the tile everywhere this is used, so the initials would only
    // repeat it.
    'aria-hidden': 'true',
    '[class.DefaultAvatar--circle]': "shape() === 'circle'",
    '[style.--fb-default-avatar-size]': 'sizePx()',
    '[style.--fb-default-avatar-initials-size]': 'initialsSizePx()',
    '[attr.data-tone]': 'tone()',
  },
})
export class DefaultAvatarComponent {
  readonly name = input<string | null | undefined>('');
  readonly shape = input<DefaultAvatarShape>('rounded');
  readonly size = input(40);

  /** Shown when the name yields no initials — an invitee who has not accepted is only an address. */
  readonly fallbackIcon = input<string | null | undefined>(null);

  protected readonly initials = computed(() => initialsFrom(this.name()));

  /** No initials, no tone: an untoned tile reads as a placeholder rather than as an identity. */
  protected readonly tone = computed(() => (this.initials() ? avatarTone(this.name()) : null));

  protected readonly sizePx = computed(() => `${this.size()}px`);

  protected readonly initialsSizePx = computed(() => `${initialsSize(this.size())}px`);
}
